import React, { useState, useEffect } from 'react';
import { pushAPI } from '../lib/api';
import { Bell, BellOff, Loader2 } from 'lucide-react';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function NotificationToggle() {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => { checkSubscription(); }, []);

  async function checkSubscription() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setIsSupported(false); setLoading(false); return;
    }
    setIsSupported(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch { setIsSubscribed(false); }
    setLoading(false);
  }

  async function handleToggle() {
    if (toggling) return;
    setToggling(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (isSubscribed) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
        await pushAPI.unsubscribe();
        setIsSubscribed(false);
      } else {
        const res = await pushAPI.getVapidKey();
        const vapidKey = res.data.public_key;
        if (!vapidKey) return;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        const subJson = sub.toJSON();
        await pushAPI.subscribe({ endpoint: subJson.endpoint, keys: subJson.keys });
        setIsSubscribed(true);
      }
    } catch (err) { console.warn('Push toggle failed:', err); }
    setToggling(false);
  }

  if (loading || !isSupported) return null;

  return (
    <button
      onClick={handleToggle}
      disabled={toggling}
      data-testid="notification-toggle"
      className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
        isSubscribed
          ? 'bg-[#2563EB]/5 border-[#2563EB]/20 hover:border-[#2563EB]/40'
          : 'bg-white border-[#E2E8F0] hover:border-[#2563EB] shadow-sm'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isSubscribed ? 'bg-[#2563EB]/10' : 'bg-[#F1F5F9]'
      }`}>
        {toggling ? (
          <Loader2 size={18} className="text-[#94A3B8] animate-spin" />
        ) : isSubscribed ? (
          <Bell size={18} className="text-[#2563EB]" />
        ) : (
          <BellOff size={18} className="text-[#94A3B8]" />
        )}
      </div>
      <div className="flex-1 text-left">
        <p className="text-sm font-semibold text-[#0A2242]">
          {isSubscribed ? 'Notifications On' : 'Enable Notifications'}
        </p>
        <p className="text-[10px] text-[#94A3B8] mt-0.5">
          {isSubscribed ? "You'll be notified of new drills & feedback" : 'Get alerts for new plans and coach feedback'}
        </p>
      </div>
      <div className={`w-10 h-6 rounded-full relative transition-colors ${
        isSubscribed ? 'bg-[#2563EB]' : 'bg-[#E2E8F0]'
      }`}>
        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${
          isSubscribed ? 'translate-x-5' : 'translate-x-1'
        }`} />
      </div>
    </button>
  );
}
